import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Settings, Users, Package, Folder, Lock, User, Trash2, AlertTriangle, Receipt, Gamepad2, Database, DollarSign, History, Edit, QrCode } from 'lucide-react';
import AdminCommerciaux from '../components/AdminCommerciaux';
import AdminOnboarding from '../components/AdminOnboarding';
import { formatDuree, getDureeSubLabel } from '../utils/formatDuration';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const Admin = () => {
  const { user: currentUser } = useAuth();
  const [rubriques, setRubriques] = useState([]);
  const [articles, setArticles] = useState([]);
  const [postes, setPostes] = useState([]);
  const [users, setUsers] = useState([]);
  const [params, setParams] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [formData, setFormData] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState({});
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [showDeleteCommercialDialog, setShowDeleteCommercialDialog] = useState(false);
  const [selectedCommercial, setSelectedCommercial] = useState(null);
  const [newCommercialName, setNewCommercialName] = useState('');
  const [showAddCommercialDialog, setShowAddCommercialDialog] = useState(false);
  const [commercialForm, setCommercialForm] = useState({
    nom: '',
    poste: 'Commercial',
    objectifCA: '',
    salaireFix: '',
    salaireBrut: '',
    telephone: '',
    dateRecrutement: new Date().toISOString().split('T')[0]
  });
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [showDeleteCategoryDialog, setShowDeleteCategoryDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  
  // Pricing states
  const [pricing, setPricing] = useState([]);
  const [pricingHistory, setPricingHistory] = useState([]);
  const [showEditPricingDialog, setShowEditPricingDialog] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    price: '',
    duration: '',
    reason: 'Ajustement'
  });
  
  // Add tranche states (CEO : nouvelle fonctionnalité d'ajout de tranche)
  const [showAddDurationDialog, setShowAddDurationDialog] = useState(false);
  const [addDurationArticle, setAddDurationArticle] = useState(null);
  const [addDurationForm, setAddDurationForm] = useState({ duration: '', price: '', reason: 'Ajout tranche' });
  
  // Delete tranche states
  const [showDeleteDurationDialog, setShowDeleteDurationDialog] = useState(false);
  const [deleteDurationTarget, setDeleteDurationTarget] = useState(null);
  
  // CEO Session 6 : Change role states
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [roleChangeTarget, setRoleChangeTarget] = useState(null);
  const [roleChangeValue, setRoleChangeValue] = useState('staff');
  
  // CEO Session 8 : Article create — Console vs Article simple
  const [articleSubType, setArticleSubType] = useState('simple'); // 'console' | 'simple'
  const [tranchesDraft, setTranchesDraft] = useState([{ duree: '', prix: '' }]);
  
  // Unit price edit states (BOISSON, NESPRESSO, CASINO)
  const [showEditUnitPriceDialog, setShowEditUnitPriceDialog] = useState(false);
  const [selectedUnitArticle, setSelectedUnitArticle] = useState(null);
  const [unitPriceForm, setUnitPriceForm] = useState({ price: '', reason: 'Ajustement' });
  
  // Connected users state
  const [connectedUsers, setConnectedUsers] = useState([]);
  
  // Ref for scrollable container
  const scrollContainerRef = React.useRef(null);
  const [jeux, setJeux] = useState([]);
  const [showDeleteJeuDialog, setShowDeleteJeuDialog] = useState(false);
  const [selectedJeu, setSelectedJeu] = useState(null);
  const [showDeleteArticleDialog, setShowDeleteArticleDialog] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showDeleteRubriqueDialog, setShowDeleteRubriqueDialog] = useState(false);
  const [selectedRubrique, setSelectedRubrique] = useState(null);
  const [rubriqueArticles, setRubriqueArticles] = useState([]);
  const [newRubriqueForArticles, setNewRubriqueForArticles] = useState('');
  const [createNewRubrique, setCreateNewRubrique] = useState(false);
  const [newRubriqueName, setNewRubriqueName] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [rubRes, artRes, postRes, userRes, paramRes, commRes, catRes, jeuxRes, connRes] = await Promise.all([
        axios.get(`${API}/rubriques`, { withCredentials: true }),
        axios.get(`${API}/articles`, { withCredentials: true }),
        axios.get(`${API}/postes`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/params`, { withCredentials: true }),
        axios.get(`${API}/commercials`, { withCredentials: true }),
        axios.get(`${API}/expense-categories`, { withCredentials: true }),
        axios.get(`${API}/jeux`, { withCredentials: true }),
        axios.get(`${API}/auth/connected-users`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setRubriques(rubRes.data);
      setArticles(artRes.data);
      setCommercials(commRes.data);
      setExpenseCategories(catRes.data);
      setJeux(jeuxRes.data);
      setPostes(postRes.data);
      setUsers(userRes.data);
      setParams(paramRes.data);
      setConnectedUsers(connRes.data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    }
  };

  const handleOpenDialog = (type, data = {}) => {
    setDialogType(type);
    setFormData(data);
    // CEO Session 8 : détecter si on édite une console ou un article simple
    if (type === 'article') {
      const isConsole = !!(data.prixParDuree && Object.keys(data.prixParDuree).length > 0);
      setArticleSubType(isConsole ? 'console' : 'simple');
      if (isConsole) {
        const rows = Object.entries(data.prixParDuree)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([d, p]) => ({ duree: d, prix: p.toString() }));
        setTranchesDraft(rows.length > 0 ? rows : [{ duree: '', prix: '' }]);
      } else {
        setTranchesDraft([{ duree: '', prix: '' }]);
      }
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (dialogType === 'rubrique') {
        if (formData.id) {
          await axios.put(`${API}/rubriques/${formData.id}`, formData, { withCredentials: true });
        } else {
          await axios.post(`${API}/rubriques`, formData, { withCredentials: true });
        }
      } else if (dialogType === 'article') {
        // CEO Session 8 : Traitement distinct Console vs Article simple
        const payload = { ...formData };
        if (articleSubType === 'console') {
          // Rubrique auto = JEUX (rubrique avec echellesTemps non vide, ou nom=JEUX)
          const jeuxRub = rubriques.find(r => r.nom?.toUpperCase() === 'JEUX')
            || rubriques.find(r => Array.isArray(r.echellesTemps) && r.echellesTemps.length > 0);
          if (!jeuxRub) {
            toast.error('Rubrique JEUX introuvable. Créez-la d\'abord dans l\'onglet Rubriques.');
            return;
          }
          // Construire prixParDuree depuis tranchesDraft
          const prixParDuree = {};
          const seen = new Set();
          for (const row of tranchesDraft) {
            const d = parseInt(row.duree, 10);
            const p = parseInt(row.prix, 10);
            if (!d || d < 10 || d > 480 || d % 10 !== 0) {
              toast.error(`Durée invalide "${row.duree}" — multiple de 10, entre 10 et 480`);
              return;
            }
            if (!p || p <= 0 || p % 100 !== 0) {
              toast.error(`Prix invalide "${row.prix}" — multiple de 100 FCFA, > 0`);
              return;
            }
            if (seen.has(d)) {
              toast.error(`Durée ${d} min en doublon`);
              return;
            }
            seen.add(d);
            prixParDuree[d.toString()] = p;
          }
          if (Object.keys(prixParDuree).length === 0) {
            toast.error('Au moins 1 tranche requise pour une console');
            return;
          }
          payload.rubriqueId = jeuxRub.id;
          payload.prixParDuree = prixParDuree;
          // Nettoyer les champs article simple
          delete payload.prixUnit;
          delete payload.stock;
          delete payload.coutAchat;
        } else {
          // Article simple : on envoie prixUnit/stock/coutAchat, pas prixParDuree
          delete payload.prixParDuree;
          delete payload.disabledDurees;
        }
        if (formData.id) {
          await axios.put(`${API}/articles/${formData.id}`, payload, { withCredentials: true });
        } else {
          await axios.post(`${API}/articles`, payload, { withCredentials: true });
        }
      } else if (dialogType === 'jeu') {
        if (formData.id) {
          await axios.put(`${API}/jeux/${formData.id}`, formData, { withCredentials: true });
        } else {
          await axios.post(`${API}/jeux`, formData, { withCredentials: true });
        }
      } else if (dialogType === 'poste') {
        if (formData.id) {
          await axios.put(`${API}/postes/${formData.id}`, formData, { withCredentials: true });
        } else {
          await axios.post(`${API}/postes`, formData, { withCredentials: true });
        }
      } else if (dialogType === 'user') {
        await axios.post(`${API}/users`, formData, { withCredentials: true });
      }
      toast.success('Enregistré !');
      setShowDialog(false);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur d\'enregistrement');
    }
  };

  const handleUpdateParams = async () => {
    try {
      await axios.put(`${API}/params`, params, { withCredentials: true });
      toast.success('Paramètres mis à jour');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de mise à jour');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoadingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, { withCredentials: true });
      
      toast.success('Mot de passe modifié avec succès !');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoadingPassword(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteUserDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await axios.delete(`${API}/users/${selectedUser.uid}`, { withCredentials: true });
      toast.success('Utilisateur supprimé');
      setShowDeleteUserDialog(false);
      setSelectedUser(null);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteJeu = (jeu) => {
    setSelectedJeu(jeu);
    setShowDeleteJeuDialog(true);
  };

  const confirmDeleteJeu = async () => {
    if (!selectedJeu) return;

    try {
      await axios.delete(`${API}/jeux/${selectedJeu.id}`, { withCredentials: true });
      toast.success('Jeu supprimé');
      setShowDeleteJeuDialog(false);
      setSelectedJeu(null);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteArticle = (article) => {
    setSelectedArticle(article);
    setShowDeleteArticleDialog(true);
  };

  const confirmDeleteArticle = async () => {
    if (!selectedArticle) return;

    try {
      await axios.delete(`${API}/articles/${selectedArticle.id}`, { withCredentials: true });
      toast.success('Article supprimé');
      setShowDeleteArticleDialog(false);
      setSelectedArticle(null);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteRubrique = async (rubrique) => {
    // Vérifier si la rubrique contient des articles
    const articlesInRubrique = articles.filter(art => art.rubriqueId === rubrique.id);
    
    if (articlesInRubrique.length > 0) {
      // Il y a des articles, afficher le dialog avec options
      setSelectedRubrique(rubrique);
      setRubriqueArticles(articlesInRubrique);
      setShowDeleteRubriqueDialog(true);
    } else {
      // Pas d'articles, supprimer directement avec confirmation
      if (window.confirm(`Êtes-vous sûr de vouloir supprimer la rubrique "${rubrique.nom}" ?`)) {
        try {
          await axios.delete(`${API}/rubriques/${rubrique.id}`, { withCredentials: true });
          toast.success('Rubrique supprimée');
          fetchAll();
        } catch (error) {
          console.error('Erreur:', error);
          toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
      }
    }
  };

  const confirmDeleteRubrique = async () => {
    if (!selectedRubrique) return;

    try {
      // Si l'utilisateur veut créer une nouvelle rubrique
      if (createNewRubrique && newRubriqueName.trim()) {
        // Créer la nouvelle rubrique
        const newRubriqueResponse = await axios.post(`${API}/rubriques`, {
          nom: newRubriqueName,
          type: 'UNITAIRE',
          actif: true,
          ordre: 0
        }, { withCredentials: true });
        
        const newRubriqueId = newRubriqueResponse.data.id;
        
        // Déplacer tous les articles vers cette nouvelle rubrique
        for (const article of rubriqueArticles) {
          await axios.put(`${API}/articles/${article.id}`, {
            ...article,
            rubriqueId: newRubriqueId
          }, { withCredentials: true });
        }
        
        toast.success(`Nouvelle rubrique "${newRubriqueName}" créée et ${rubriqueArticles.length} article(s) déplacé(s)`);
      } else if (newRubriqueForArticles) {
        // Déplacer tous les articles vers la rubrique sélectionnée
        for (const article of rubriqueArticles) {
          await axios.put(`${API}/articles/${article.id}`, {
            ...article,
            rubriqueId: newRubriqueForArticles
          }, { withCredentials: true });
        }
        
        toast.success(`${rubriqueArticles.length} article(s) déplacé(s)`);
      } else {
        toast.error('Veuillez sélectionner une rubrique ou créer une nouvelle');
        return;
      }

      // Supprimer la rubrique
      await axios.delete(`${API}/rubriques/${selectedRubrique.id}`, { withCredentials: true });
      toast.success('Rubrique supprimée');
      
      // Reset et fermer
      setShowDeleteRubriqueDialog(false);
      setSelectedRubrique(null);
      setRubriqueArticles([]);
      setNewRubriqueForArticles('');
      setCreateNewRubrique(false);
      setNewRubriqueName('');
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleAddCommercial = async () => {
    // Validation
    if (!commercialForm.nom.trim()) {
      toast.error('⚠️ Le nom est obligatoire');
      return;
    }
    if (!commercialForm.objectifCA || commercialForm.objectifCA <= 0) {
      toast.error('⚠️ L\'objectif mensuel doit être supérieur à 0');
      return;
    }
    if (!commercialForm.salaireFix || commercialForm.salaireFix <= 0) {
      toast.error('⚠️ Le salaire fixe doit être supérieur à 0');
      return;
    }
    if (!commercialForm.salaireBrut || commercialForm.salaireBrut <= 0) {
      toast.error('⚠️ Le salaire brut doit être supérieur à 0');
      return;
    }

    try {
      const payload = {
        nom: commercialForm.nom.trim(),
        poste: commercialForm.poste,
        objectifCA: parseInt(commercialForm.objectifCA),
        salaireFix: parseInt(commercialForm.salaireFix),
        salaireBrut: parseInt(commercialForm.salaireBrut),
        telephone: commercialForm.telephone.trim() || null,
        dateRecrutement: commercialForm.dateRecrutement
      };
      
      await axios.post(`${API}/commercials`, payload, { withCredentials: true });
      toast.success('✅ Commercial ajouté avec succès');
      
      // Reset form
      setCommercialForm({
        nom: '',
        poste: 'Commercial',
        objectifCA: '',
        salaireFix: '',
        salaireBrut: '',
        telephone: '',
        dateRecrutement: new Date().toISOString().split('T')[0]
      });
      setShowAddCommercialDialog(false);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('❌ Erreur lors de l\'ajout');
    }
  };

  const confirmDeleteCommercial = async () => {
    if (!selectedCommercial) return;

    try {
      await axios.delete(`${API}/commercials/${selectedCommercial.id}`, { withCredentials: true });
      toast.success('🗑️ Commercial supprimé avec succès');
      setShowDeleteCommercialDialog(false);
      setSelectedCommercial(null);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('❌ ' + (error.response?.data?.detail || 'Échec de la suppression'));
    }
  };

  const handleInitCommercials = async () => {
    try {
      const response = await axios.post(`${API}/commercials/init`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation');
    }
  };

  // ============= PRICING MANAGEMENT =============
  
  const fetchPricingHistory = async () => {
    try {
      const response = await axios.get(`${API}/articles/pricing-history`, { withCredentials: true });
      setPricingHistory(response.data);
    } catch (error) {
      console.error('Erreur historique prix:', error);
    }
  };

  const handleEditPricing = (article, durationKey, currentPrice) => {
    setSelectedPricing({ articleId: article.id, articleName: article.nom, duration: durationKey, currentPrice });
    setPricingForm({
      price: currentPrice,
      duration: parseInt(durationKey),
      reason: 'Ajustement'
    });
    setShowEditPricingDialog(true);
  };

  const handleUpdatePricing = async () => {
    if (!selectedPricing) return;
    const priceVal = parseInt(pricingForm.price);
    const newDurationVal = parseInt(pricingForm.duration);
    
    if (!priceVal || priceVal <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    if (priceVal % 100 !== 0) {
      toast.error('Le prix doit être un multiple de 100 FCFA');
      return;
    }
    if (!newDurationVal || newDurationVal < 10 || newDurationVal > 480) {
      toast.error('La durée doit être entre 10 et 480 minutes');
      return;
    }
    if (newDurationVal % 10 !== 0) {
      toast.error('La durée doit être un multiple de 10 minutes');
      return;
    }

    try {
      await axios.put(`${API}/articles/${selectedPricing.articleId}/update-duration`, {
        old_duration: selectedPricing.duration,
        new_duration: newDurationVal,
        new_price: priceVal,
        reason: pricingForm.reason
      }, { withCredentials: true });
      toast.success(`Tranche mise à jour : ${selectedPricing.articleName} — ${formatDuree(newDurationVal)} / ${priceVal.toLocaleString()} F`);
      
      // Notify other tabs/pages about pricing update
      localStorage.setItem('pricing_updated', Date.now().toString());
      
      setShowEditPricingDialog(false);
      setPricingForm({ price: '', duration: '', reason: 'Ajustement' });
      setSelectedPricing(null);
      fetchAll();
      fetchPricingHistory();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  // CEO : Ajouter une nouvelle tranche à un jeu
  const handleOpenAddDuration = (article) => {
    setAddDurationArticle(article);
    setAddDurationForm({ duration: '', price: '', reason: 'Ajout tranche' });
    setShowAddDurationDialog(true);
  };

  const handleSubmitAddDuration = async () => {
    if (!addDurationArticle) return;
    const d = parseInt(addDurationForm.duration);
    const p = parseInt(addDurationForm.price);
    if (!d || d < 10 || d > 480) {
      toast.error('La durée doit être entre 10 et 480 minutes');
      return;
    }
    if (d % 10 !== 0) {
      toast.error('La durée doit être un multiple de 10 minutes');
      return;
    }
    if (!p || p <= 0 || p % 100 !== 0) {
      toast.error('Le prix doit être un multiple de 100 FCFA');
      return;
    }
    try {
      await axios.post(`${API}/articles/${addDurationArticle.id}/add-duration`, {
        duration: d,
        price: p,
        reason: addDurationForm.reason
      }, { withCredentials: true });
      toast.success(`Tranche ajoutée : ${addDurationArticle.nom} — ${formatDuree(d)} / ${p.toLocaleString()} F`);
      localStorage.setItem('pricing_updated', Date.now().toString());
      setShowAddDurationDialog(false);
      setAddDurationArticle(null);
      setAddDurationForm({ duration: '', price: '', reason: 'Ajout tranche' });
      fetchAll();
      fetchPricingHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
    }
  };

  // CEO Session 5 : Supprimer une tranche
  const handleOpenDeleteDuration = (article, dureeKey) => {
    setDeleteDurationTarget({ articleId: article.id, articleName: article.nom, duration: dureeKey });
    setShowDeleteDurationDialog(true);
  };

  const handleConfirmDeleteDuration = async () => {
    if (!deleteDurationTarget) return;
    try {
      await axios.delete(
        `${API}/articles/${deleteDurationTarget.articleId}/remove-duration/${deleteDurationTarget.duration}`,
        { withCredentials: true }
      );
      toast.success(`Tranche ${formatDuree(deleteDurationTarget.duration)} supprimée de ${deleteDurationTarget.articleName}`);
      localStorage.setItem('pricing_updated', Date.now().toString());
      setShowDeleteDurationDialog(false);
      setDeleteDurationTarget(null);
      fetchAll();
      fetchPricingHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // CEO Session 5 : Activer / désactiver une tranche (visibilité Ventes & Prestations)
  const handleToggleDuration = async (article, dureeKey) => {
    try {
      const res = await axios.patch(
        `${API}/articles/${article.id}/toggle-duration/${dureeKey}`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      localStorage.setItem('pricing_updated', Date.now().toString());
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  // CEO Session 6 : Changer le rôle d'un utilisateur (admin ↔ cashier ↔ staff)
  const handleOpenChangeRole = (usr) => {
    setRoleChangeTarget(usr);
    setRoleChangeValue(usr.role || 'staff');
    setShowChangeRoleDialog(true);
  };

  const handleConfirmChangeRole = async () => {
    if (!roleChangeTarget) return;
    if (roleChangeValue === roleChangeTarget.role) {
      toast.info('Aucun changement — même rôle sélectionné');
      setShowChangeRoleDialog(false);
      return;
    }
    try {
      await axios.put(
        `${API}/users/${roleChangeTarget.uid}`,
        { role: roleChangeValue },
        { withCredentials: true }
      );
      const oldLabel = { admin: 'Admin', cashier: 'Manager (Cashier)', staff: 'Commercial (Staff)' }[roleChangeTarget.role] || roleChangeTarget.role;
      const newLabel = { admin: 'Admin', cashier: 'Manager (Cashier)', staff: 'Commercial (Staff)' }[roleChangeValue] || roleChangeValue;
      toast.success(`${roleChangeTarget.displayName} : ${oldLabel} → ${newLabel}`);
      setShowChangeRoleDialog(false);
      setRoleChangeTarget(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de rôle');
    }
  };

  const handleRestorePricing = async (historyId) => {
    if (!window.confirm('Voulez-vous restaurer ce prix ?')) return;

    try {
      await axios.post(`${API}/pricing/restore/${historyId}`, {}, { withCredentials: true });
      toast.success('✅ Prix restauré avec succès');
      fetchAll();
      fetchPricingHistory();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('❌ Erreur lors de la restauration');
    }
  };

  const handleInitPricing = async () => {
    try {
      const response = await axios.post(`${API}/pricing/init`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation');
    }
  };

  // Unit price handlers (BOISSON, NESPRESSO, CASINO)
  const handleEditUnitPrice = (article) => {
    setSelectedUnitArticle(article);
    setUnitPriceForm({ price: article.prixUnit || 0, reason: 'Ajustement' });
    setShowEditUnitPriceDialog(true);
  };

  const handleUpdateUnitPrice = async () => {
    if (!selectedUnitArticle) return;
    const priceVal = parseInt(unitPriceForm.price);
    if (!priceVal || priceVal <= 0) {
      toast.error('Le prix doit être supérieur à 0');
      return;
    }
    if (priceVal % 100 !== 0) {
      toast.error('Le prix doit être un multiple de 100 FCFA');
      return;
    }
    try {
      await axios.put(`${API}/articles/${selectedUnitArticle.id}/update-unit-price`, {
        price: priceVal,
        reason: unitPriceForm.reason
      }, { withCredentials: true });
      toast.success(`Prix mis à jour: ${selectedUnitArticle.nom} = ${priceVal} FCFA`);
      localStorage.setItem('pricing_updated', Date.now().toString());
      setShowEditUnitPriceDialog(false);
      setSelectedUnitArticle(null);
      fetchAll();
      fetchPricingHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Veuillez entrer un nom');
      return;
    }

    try {
      await axios.post(`${API}/expense-categories`, { nom: newCategoryName }, { withCredentials: true });
      toast.success('Catégorie ajoutée');
      setNewCategoryName('');
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const confirmDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      await axios.delete(`${API}/expense-categories/${selectedCategory.id}`, { withCredentials: true });
      toast.success('Catégorie supprimée');
      setShowDeleteCategoryDialog(false);
      setSelectedCategory(null);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleInitCategories = async () => {
    try {
      const response = await axios.post(`${API}/expense-categories/init`, {}, { withCredentials: true });
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'initialisation');
    }
  };

  const handleReset = async (type) => {
    const confirmMessages = {
      transactions: 'Êtes-vous sûr de vouloir supprimer TOUTES les transactions ? Cette action est irréversible.',
      sessions: 'Êtes-vous sûr de vouloir supprimer TOUTES les sessions ? Cette action est irréversible.',
      expenses: 'Êtes-vous sûr de vouloir supprimer TOUTES les dépenses ? Cette action est irréversible.',
      tournaments: 'Êtes-vous sûr de vouloir supprimer TOUS les tournois ? Cette action est irréversible.',
      all: '⚠️ ATTENTION : Vous allez supprimer TOUTES les données (transactions, sessions, dépenses, tournois). Cette action est IRRÉVERSIBLE. Êtes-vous absolument sûr ?'
    };

    if (!window.confirm(confirmMessages[type])) return;

    setResetLoading({ ...resetLoading, [type]: true });
    try {
      const response = await axios.delete(`${API}/admin/reset/${type}`, { withCredentials: true });
      toast.success(response.data.message);
      
      // Show details
      const details = [];
      if (response.data.transactions) details.push(`${response.data.transactions} transactions`);
      if (response.data.sessions) details.push(`${response.data.sessions} sessions`);
      if (response.data.expenses) details.push(`${response.data.expenses} dépenses`);
      if (response.data.tournaments) details.push(`${response.data.tournaments} tournois`);
      
      if (details.length > 0) {
        toast.info(`Supprimé : ${details.join(', ')}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResetLoading({ ...resetLoading, [type]: false });
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <h1 className="text-3xl lg:text-4xl font-bold text-[#EAEAEA]">Administration</h1>

      <Tabs defaultValue="profil" className="space-y-4">
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="bg-[#1C2541] flex flex-nowrap md:flex-wrap gap-2 min-w-max md:min-w-0">
            <TabsTrigger value="onboarding" data-testid="tab-onboarding-top" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-[#0B132B] whitespace-nowrap font-bold border border-[#FFD700]/40">
              <QrCode className="w-4 h-4 mr-2" />
              📱 Onboarding équipe
            </TabsTrigger>
            <TabsTrigger value="profil" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Users className="w-4 h-4 mr-2" />
              Mon Profil
            </TabsTrigger>
            <TabsTrigger value="rubriques" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Folder className="w-4 h-4 mr-2" />
              Rubriques
            </TabsTrigger>
            <TabsTrigger value="articles" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Package className="w-4 h-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="jeux" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Gamepad2 className="w-4 h-4 mr-2" />
              Jeux
            </TabsTrigger>
            <TabsTrigger value="expense-categories" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Receipt className="w-4 h-4 mr-2" />
              Types de Dépense
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="commercials" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <User className="w-4 h-4 mr-2" />
              Commerciaux
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <DollarSign className="w-4 h-4 mr-2" />
              Gestion des Prix
            </TabsTrigger>
            <TabsTrigger value="connected" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white whitespace-nowrap">
              <Users className="w-4 h-4 mr-2" />
              En ligne
            </TabsTrigger>
            <TabsTrigger value="params" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </TabsTrigger>
            <TabsTrigger value="reset" className="data-[state=active]:bg-[#C6A15B] data-[state=active]:text-[#0B132B] whitespace-nowrap">
              <Trash2 className="w-4 h-4 mr-2" />
              Réinitialisation
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="profil" className="space-y-4">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Informations du profil */}
            <Card className="glass-effect border-[#C6A15B]/20">
              <CardHeader>
                <CardTitle className="text-[#EAEAEA] flex items-center gap-2">
                  <User className="w-5 h-5 text-[#C6A15B]" />
                  Informations du Profil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#EAEAEA] text-sm">Nom complet</Label>
                  <p className="text-[#C6A15B] font-semibold mt-1">{currentUser?.displayName}</p>
                </div>
                <div>
                  <Label className="text-[#EAEAEA] text-sm">Email</Label>
                  <p className="text-[#C6A15B] font-semibold mt-1">{currentUser?.email}</p>
                </div>
                <div>
                  <Label className="text-[#EAEAEA] text-sm">Rôle</Label>
                  <p className="text-[#C6A15B] font-semibold mt-1 capitalize">{currentUser?.role}</p>
                </div>
              </CardContent>
            </Card>

            {/* Changement de mot de passe */}
            <Card className="glass-effect border-[#C6A15B]/20">
              <CardHeader>
                <CardTitle className="text-[#EAEAEA] flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#C6A15B]" />
                  Changer le Mot de Passe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="current-password" className="text-[#EAEAEA]">
                      Mot de passe actuel *
                    </Label>
                    <Input
                      id="current-password"
                      type="password"
                      required
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] mt-1"
                      placeholder="••••••••"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-password" className="text-[#EAEAEA]">
                      Nouveau mot de passe *
                    </Label>
                    <Input
                      id="new-password"
                      type="password"
                      required
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] mt-1"
                      placeholder="Min 6 caractères"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password" className="text-[#EAEAEA]">
                      Confirmer le nouveau mot de passe *
                    </Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      required
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] mt-1"
                      placeholder="Répéter le mot de passe"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loadingPassword}
                    className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B] font-semibold"
                  >
                    {loadingPassword ? 'Modification...' : 'Modifier le mot de passe'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Message d'information */}
          <Card className="glass-effect border-[#C6A15B]/20 bg-[#C6A15B]/10">
            <CardContent className="p-4">
              <p className="text-[#EAEAEA] text-sm">
                <strong className="text-[#C6A15B]">💡 Conseil de sécurité :</strong> Utilisez un mot de passe fort contenant au moins 6 caractères. 
                Changez régulièrement votre mot de passe pour assurer la sécurité de votre compte.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rubriques" className="space-y-4">
          <Button
            onClick={() => handleOpenDialog('rubrique', { type: 'UNITAIRE', actif: true, ordre: 0 })}
            className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Rubrique
          </Button>
          <div className="grid gap-4">
            {rubriques.map(rub => (
              <Card key={rub.id} className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-[#EAEAEA]">{rub.nom}</h3>
                    <p className="text-[#EAEAEA]/60 text-sm">Type: {rub.type}</p>
                    <p className="text-[#EAEAEA]/60 text-sm">Actif: {rub.actif ? 'Oui' : 'Non'}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleOpenDialog('rubrique', rub)}
                      size="sm"
                      className="bg-[#1C2541] hover:bg-[#C6A15B] text-[#EAEAEA] hover:text-[#0B132B]"
                    >
                      Modifier
                    </Button>
                    <Button
                      onClick={() => handleDeleteRubrique(rub)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="articles" className="space-y-4">
          <Button
            data-testid="btn-nouvel-article"
            onClick={() => handleOpenDialog('article', { actif: true })}
            className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel Article
          </Button>
          
          {/* CEO Session 8 : Section CONSOLES */}
          <div className="space-y-3">
            <h3 className="text-[#FFD700] font-bold text-lg flex items-center gap-2 pt-2 border-b border-[#FFD700]/20 pb-2">
              <Gamepad2 className="w-5 h-5" />
              Consoles <span className="text-[#EAEAEA]/50 text-sm font-normal">(tranches horaires — apparaissent dans JEUX)</span>
            </h3>
            <div className="grid gap-4">
              {articles.filter(a => a.prixParDuree && Object.keys(a.prixParDuree).length > 0).map(art => (
                <Card key={art.id} className="glass-effect border-[#FFD700]/25 bg-gradient-to-br from-[#1C2541]/70 to-[#2A3B5C]/40">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 font-bold uppercase tracking-wide">Console</span>
                        <h3 className="text-lg font-bold text-[#EAEAEA]">{art.nom}</h3>
                      </div>
                      <p className="text-[#EAEAEA]/70 text-sm">
                        {Object.keys(art.prixParDuree).length} tranche{Object.keys(art.prixParDuree).length > 1 ? 's' : ''} — {Object.entries(art.prixParDuree).sort(([a], [b]) => parseInt(a) - parseInt(b)).map(([d, p]) => `${d}min/${p.toLocaleString()}F`).join(' • ')}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        data-testid={`edit-console-${art.nom}`}
                        onClick={() => handleOpenDialog('article', art)}
                        size="sm"
                        className="bg-[#1C2541] hover:bg-[#C6A15B] text-[#EAEAEA] hover:text-[#0B132B]"
                      >
                        Modifier
                      </Button>
                      <Button
                        onClick={() => handleDeleteArticle(art)}
                        size="sm"
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {articles.filter(a => a.prixParDuree && Object.keys(a.prixParDuree).length > 0).length === 0 && (
                <p className="text-[#EAEAEA]/50 text-sm italic text-center py-4">Aucune console. Cliquez sur "Nouvel Article" → type "Console".</p>
              )}
            </div>
          </div>
          
          {/* CEO Session 8 : Section ARTICLES SIMPLES */}
          <div className="space-y-3 mt-6">
            <h3 className="text-[#C6A15B] font-bold text-lg flex items-center gap-2 pt-2 border-b border-[#C6A15B]/20 pb-2">
              <Package className="w-5 h-5" />
              Articles simples <span className="text-[#EAEAEA]/50 text-sm font-normal">(prix fixe — Boisson, Café, Casino…)</span>
            </h3>
            <div className="grid gap-4">
              {articles.filter(a => !(a.prixParDuree && Object.keys(a.prixParDuree).length > 0)).map(art => {
                const rubName = rubriques.find(r => r.id === art.rubriqueId)?.nom || '—';
                return (
                <Card key={art.id} className="glass-effect border-[#C6A15B]/20">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#C6A15B]/20 text-[#C6A15B] border border-[#C6A15B]/40 font-bold uppercase tracking-wide">{rubName}</span>
                        <h3 className="text-lg font-bold text-[#EAEAEA]">{art.nom}</h3>
                      </div>
                      {art.prixUnit ? <p className="text-[#C6A15B] text-sm">Prix: {art.prixUnit.toLocaleString()} FCFA{art.stock !== undefined && art.stock !== null ? ` • Stock: ${art.stock}` : ''}</p> : <p className="text-[#EAEAEA]/50 text-sm italic">Prix non défini</p>}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleOpenDialog('article', art)}
                        size="sm"
                        className="bg-[#1C2541] hover:bg-[#C6A15B] text-[#EAEAEA] hover:text-[#0B132B]"
                      >
                        Modifier
                      </Button>
                      <Button
                        onClick={() => handleDeleteArticle(art)}
                        size="sm"
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
              {articles.filter(a => !(a.prixParDuree && Object.keys(a.prixParDuree).length > 0)).length === 0 && (
                <p className="text-[#EAEAEA]/50 text-sm italic text-center py-4">Aucun article simple.</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="jeux" className="space-y-4">
          <Button
            onClick={() => handleOpenDialog('jeu', { console: 'PS5', actif: true })}
            className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Jeu
          </Button>
          <div className="grid gap-4">
            {jeux.map(jeu => (
              <Card key={jeu.id} className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-[#EAEAEA]">{jeu.nom}</h3>
                    <p className="text-[#C6A15B]">Console: {jeu.console}</p>
                    {jeu.prix && <p className="text-[#C6A15B]">Prix: {jeu.prix} FCFA</p>}
                    <span className={`text-xs ${jeu.actif ? 'text-green-400' : 'text-red-400'}`}>
                      {jeu.actif ? '● Actif' : '● Inactif'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleOpenDialog('jeu', jeu)}
                      size="sm"
                      className="bg-[#1C2541] hover:bg-[#C6A15B] text-[#EAEAEA] hover:text-[#0B132B]"
                    >
                      Modifier
                    </Button>
                    <Button
                      onClick={() => handleDeleteJeu(jeu)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <Button
            onClick={() => handleOpenDialog('user', { role: 'staff' })}
            className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel Utilisateur
          </Button>
          <div className="grid gap-4">
            {users.map(usr => {
              const roleLabel = { admin: 'Admin', cashier: 'Manager (Cashier)', staff: 'Commercial (Staff)' }[usr.role] || usr.role;
              const roleColor = { admin: 'text-[#FFD700]', cashier: 'text-[#10B981]', staff: 'text-[#C6A15B]' }[usr.role] || 'text-[#C6A15B]';
              return (
              <Card key={usr.uid} className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-[#EAEAEA]">{usr.displayName}</h3>
                      <p className="text-[#EAEAEA]/60 text-sm">{usr.email}</p>
                      <p className={`${roleColor} text-sm font-semibold mt-1`}>Rôle : {roleLabel}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {usr.uid !== currentUser?.uid && (
                        <Button
                          data-testid={`change-role-${usr.email}`}
                          onClick={() => handleOpenChangeRole(usr)}
                          size="sm"
                          className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFC700] hover:to-[#FF9500] text-[#0B132B] font-bold"
                        >
                          Changer le rôle
                        </Button>
                      )}
                      {usr.uid !== currentUser?.uid && (
                        <Button
                          onClick={() => handleDeleteUser(usr)}
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          data-testid={`delete-user-${usr.email}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="commercials" className="space-y-4">
          {/* Bouton de Migration DEV → PROD */}
          <Card className="glass-effect border-[#10B981]/30 mb-4 bg-gradient-to-r from-[#10B981]/10 to-[#059669]/10">
            <CardHeader>
              <CardTitle className="text-[#10B981] flex items-center gap-2">
                <Database className="w-5 h-5" />
                Migration DEV → PROD
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#EAEAEA]/70 text-sm mb-4">
                Importez Kokora N'GUESSAN et Ange ROLLAND avec leurs transactions en un clic.
              </p>
              <Button
                onClick={async () => {
                  try {
                    const response = await fetch(`${API}/admin/migration-one-click`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' }
                    });
                    
                    const result = await response.json();
                    
                    if (result.success) {
                      toast.success(`✅ Migration Réussie ! ${result.commerciaux_crees.length} commerciaux créés : ${result.commerciaux_crees.join(', ')}`);
                      fetchCommercials();
                    } else {
                      // Correction : Vérifier si errors existe et est un tableau
                      const errorMessage = result.errors && Array.isArray(result.errors) 
                        ? result.errors.join(', ')
                        : result.message || 'Erreur lors de la migration';
                      toast.error(`⚠️ Erreur : ${errorMessage}`);
                    }
                  } catch (error) {
                    console.error('Erreur migration:', error);
                    toast.error(`❌ Erreur : ${error.message}`);
                  }
                }}
                className="bg-[#10B981] hover:bg-[#059669] text-white"
              >
                <Database className="w-4 h-4 mr-2" />
                🚀 LANCER LA MIGRATION
              </Button>
            </CardContent>
          </Card>

          <Card className="glass-effect border-[#C6A15B]/20 mb-4">
            <CardContent className="p-4">
              <Button
                onClick={() => setShowAddCommercialDialog(true)}
                className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFC700] hover:to-[#FF9500] text-[#0B132B] font-bold text-lg py-6"
              >
                <Plus className="w-5 h-5 mr-2" />
                Ajouter un Commercial
              </Button>
            </CardContent>
          </Card>

          <div className="grid gap-4">
            {commercials.map(commercial => (
              <Card key={commercial.id} className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-[#EAEAEA]">{commercial.nom}</h3>
                      <p className="text-[#C6A15B]/60 text-sm">
                        Ajouté le {new Date(commercial.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedCommercial(commercial);
                        setShowDeleteCommercialDialog(true);
                      }}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {commercials.length === 0 && (
              <Card className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-8 text-center">
                  <p className="text-[#EAEAEA]/60">Aucun commercial enregistré</p>
                  <Button
                    onClick={handleInitCommercials}
                    className="mt-4 bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
                  >
                    Initialiser l'équipe PlayCity
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="expense-categories" className="space-y-4">
          <Card className="glass-effect border-[#C6A15B]/20 mb-4">
            <CardHeader>
              <CardTitle className="text-[#EAEAEA]">Ajouter un Type de Dépense</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  placeholder="Nom de la catégorie (ex: CIE, LOYER...)"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]"
                />
                <Button
                  onClick={handleAddCategory}
                  className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4">
            {expenseCategories.map(category => (
              <Card key={category.id} className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-[#EAEAEA]">{category.nom}</h3>
                      <p className="text-[#C6A15B]/60 text-sm">
                        Ajouté le {new Date(category.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowDeleteCategoryDialog(true);
                      }}
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {expenseCategories.length === 0 && (
              <Card className="glass-effect border-[#C6A15B]/20">
                <CardContent className="p-8 text-center">
                  <p className="text-[#EAEAEA]/60">Aucun type de dépense enregistré</p>
                  <Button
                    onClick={handleInitCategories}
                    className="mt-4 bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
                  >
                    Initialiser les Catégories par Défaut
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="params" className="space-y-4">
          {params && (
            <Card className="glass-effect border-[#C6A15B]/20">
              <CardHeader>
                <CardTitle className="text-[#EAEAEA]">Paramètres du Site</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-[#EAEAEA]">Monnaie</Label>
                  <Input
                    value={params.monnaie}
                    onChange={(e) => setParams({ ...params, monnaie: e.target.value })}
                    className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]"
                  />
                </div>
                <div>
                  <Label className="text-[#EAEAEA]">Encaissement Jeux</Label>
                  <Select
                    value={params.encaissementJeux}
                    onValueChange={(value) => setParams({ ...params, encaissementJeux: value })}
                  >
                    <SelectTrigger className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                      <SelectItem value="prepay">Prépayé</SelectItem>
                      <SelectItem value="postpay">Postpayé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleUpdateParams}
                  className="bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
                >
                  Enregistrer
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="onboarding" className="space-y-4">
          <AdminOnboarding />
        </TabsContent>

        <TabsContent value="reset" className="space-y-4">
          <Card className="glass-effect border-red-500/30 bg-red-900/10">
            <CardHeader>
              <CardTitle className="text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Zone de Réinitialisation - ATTENTION
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-[#EAEAEA]/80 text-sm">
                ⚠️ Les actions ci-dessous sont IRRÉVERSIBLES. Utilisez-les uniquement pour effacer les données de test avant la mise en production.
              </p>

              <div className="space-y-4">
                {/* Reset Transactions */}
                <Card className="bg-[#0B132B] border-[#C6A15B]/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-[#EAEAEA] font-bold">Supprimer les Transactions</h3>
                        <p className="text-[#EAEAEA]/60 text-sm">Supprime toutes les ventes et leurs items</p>
                      </div>
                      <Button
                        onClick={() => handleReset('transactions')}
                        disabled={resetLoading.transactions}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {resetLoading.transactions ? 'Suppression...' : 'Supprimer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Reset Sessions */}
                <Card className="bg-[#0B132B] border-[#C6A15B]/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-[#EAEAEA] font-bold">Supprimer les Sessions</h3>
                        <p className="text-[#EAEAEA]/60 text-sm">Supprime toutes les sessions et réinitialise les postes</p>
                      </div>
                      <Button
                        onClick={() => handleReset('sessions')}
                        disabled={resetLoading.sessions}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {resetLoading.sessions ? 'Suppression...' : 'Supprimer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Reset Expenses */}
                <Card className="bg-[#0B132B] border-[#C6A15B]/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-[#EAEAEA] font-bold">Supprimer les Dépenses</h3>
                        <p className="text-[#EAEAEA]/60 text-sm">Supprime toutes les dépenses enregistrées</p>
                      </div>
                      <Button
                        onClick={() => handleReset('expenses')}
                        disabled={resetLoading.expenses}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {resetLoading.expenses ? 'Suppression...' : 'Supprimer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Reset Tournaments */}
                <Card className="bg-[#0B132B] border-[#C6A15B]/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-[#EAEAEA] font-bold">Supprimer les Tournois</h3>
                        <p className="text-[#EAEAEA]/60 text-sm">Supprime tous les tournois enregistrés</p>
                      </div>
                      <Button
                        onClick={() => handleReset('tournaments')}
                        disabled={resetLoading.tournaments}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {resetLoading.tournaments ? 'Suppression...' : 'Supprimer'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Reset All */}
                <Card className="bg-red-900/20 border-red-500/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-red-400 font-bold flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          TOUT RÉINITIALISER
                        </h3>
                        <p className="text-[#EAEAEA]/60 text-sm">Supprime TOUTES les données (transactions, sessions, dépenses, tournois)</p>
                      </div>
                      <Button
                        onClick={() => handleReset('all')}
                        disabled={resetLoading.all}
                        className="bg-red-700 hover:bg-red-800 text-white border-2 border-red-500"
                      >
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        {resetLoading.all ? 'Suppression...' : 'TOUT SUPPRIMER'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-[#C6A15B]/20 border border-[#C6A15B] rounded-lg p-4">
                <p className="text-[#EAEAEA] text-sm">
                  <strong>Note importante :</strong> La réinitialisation NE supprime PAS :
                </p>
                <ul className="text-[#EAEAEA]/80 text-sm mt-2 space-y-1 list-disc list-inside">
                  <li>Les utilisateurs (admin, cashiers, staff)</li>
                  <li>Les rubriques (PS4, PS5, Quest 3, VR2, etc.)</li>
                  <li>Les articles</li>
                  <li>Les postes de jeu</li>
                  <li>Les paramètres du site</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Commerciaux */}
        <TabsContent value="commercials">
          <AdminCommerciaux />
        </TabsContent>

        {/* Onglet Gestion des Prix */}
        <TabsContent value="pricing" className="space-y-4">
          <Card className="glass-effect border-[#FFD700]/20">
            <CardHeader>
              <CardTitle className="text-[#FFD700] text-2xl font-bold">Gestion des Prix des Jeux</CardTitle>
            </CardHeader>
            <CardContent>
              {articles.filter(a => a.prixParDuree && Object.keys(a.prixParDuree).length > 0).length > 0 ? (
                <div className="space-y-6">
                  {articles.filter(a => a.prixParDuree && Object.keys(a.prixParDuree).length > 0).map(article => (
                    <div key={article.id}>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-[#FFD700] font-bold text-lg flex items-center gap-2">
                          <Gamepad2 className="w-5 h-5" />
                          {article.nom}
                        </h3>
                        <Button
                          data-testid={`add-duration-${article.nom.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => handleOpenAddDuration(article)}
                          size="sm"
                          className="bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] border border-[#10B981]/40"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Ajouter une tranche
                        </Button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                        {Object.entries(article.prixParDuree)
                          .sort(([a], [b]) => parseInt(a) - parseInt(b))
                          .map(([dureeKey, prix]) => {
                          const subLabel = getDureeSubLabel(dureeKey);
                          const isDisabled = (article.disabledDurees || []).includes(dureeKey);
                          return (
                          <Card
                            key={`${article.id}-${dureeKey}`}
                            className={`bg-[#1C2541]/50 border-[#FFD700]/30 ${isDisabled ? 'opacity-60' : ''}`}
                            data-testid={`pricing-card-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                          >
                            <CardContent className="p-4 space-y-2">
                              <div className="flex justify-between items-start">
                                <span className="text-[#EAEAEA]/70">Durée:</span>
                                <div className="text-right">
                                  <div className="text-[#EAEAEA] font-bold">{formatDuree(dureeKey)}</div>
                                  {subLabel && <div className="text-[#C6A15B] text-xs italic">{subLabel}</div>}
                                  <div className="text-[#EAEAEA]/40 text-xs">({dureeKey} min)</div>
                                </div>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-[#EAEAEA]/70">Prix:</span>
                                <span className="text-[#10B981] font-bold text-xl">{prix.toLocaleString()} F</span>
                              </div>
                              {/* CEO Session 5 — Badge statut */}
                              <div className="flex justify-between items-center pt-1">
                                <span className="text-[#EAEAEA]/70 text-sm">Statut:</span>
                                {isDisabled ? (
                                  <span
                                    data-testid={`status-inactive-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                                    className="px-2 py-0.5 rounded-full bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/40 text-xs font-bold uppercase tracking-wide"
                                  >
                                    Inactif
                                  </span>
                                ) : (
                                  <span
                                    data-testid={`status-active-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                                    className="px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 text-xs font-bold uppercase tracking-wide"
                                  >
                                    Actif
                                  </span>
                                )}
                              </div>
                              <Button
                                data-testid={`edit-price-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                                onClick={() => handleEditPricing(article, dureeKey, prix)}
                                className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFC700] hover:to-[#FF9500] text-[#0B132B] font-bold"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </Button>
                              {/* CEO Session 5 — Toggle actif/inactif */}
                              <Button
                                data-testid={`toggle-duration-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                                onClick={() => handleToggleDuration(article, dureeKey)}
                                variant="outline"
                                className={`w-full ${isDisabled
                                  ? 'border-[#10B981]/40 text-[#10B981] hover:bg-[#10B981]/10'
                                  : 'border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10'}`}
                              >
                                {isDisabled ? 'Activer' : 'Désactiver'}
                              </Button>
                              <Button
                                data-testid={`delete-duration-${article.nom.toLowerCase().replace(/\s+/g, '-')}-${dureeKey}`}
                                onClick={() => handleOpenDeleteDuration(article, dureeKey)}
                                variant="outline"
                                className="w-full border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </Button>
                            </CardContent>
                          </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Gamepad2 className="w-16 h-16 mx-auto text-[#FFD700]/30 mb-4" />
                  <p className="text-[#EAEAEA]/60 text-lg">Aucun article avec tarif horaire configuré</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Articles à Prix Fixe (BOISSON, NESPRESSO, CASINO) */}
          <Card className="glass-effect border-[#FFD700]/20">
            <CardHeader>
              <CardTitle className="text-[#FFD700] text-2xl font-bold">Prix des Articles (Boisson, Nespresso, Casino)</CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const fixedArticles = articles.filter(a => a.prixUnit && !a.prixParDuree);
                const grouped = {};
                fixedArticles.forEach(a => {
                  const rub = rubriques.find(r => r.id === a.rubriqueId);
                  const rubName = rub ? rub.nom : 'Autre';
                  if (!grouped[rubName]) grouped[rubName] = [];
                  grouped[rubName].push(a);
                });
                
                return Object.keys(grouped).length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(grouped).map(([rubName, arts]) => (
                      <div key={rubName}>
                        <h3 className="text-[#FFD700] font-bold text-lg mb-3">{rubName}</h3>
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                          {arts.map(article => (
                            <Card key={article.id} className="bg-[#1C2541]/50 border-[#FFD700]/30">
                              <CardContent className="p-4 space-y-2">
                                <span className="text-[#EAEAEA] font-bold text-lg">{article.nom}</span>
                                <div className="flex justify-between items-center">
                                  <span className="text-[#EAEAEA]/70">Prix:</span>
                                  <span className="text-[#10B981] font-bold text-xl">{article.prixUnit?.toLocaleString()} F</span>
                                </div>
                                <Button
                                  data-testid={`edit-unit-price-${article.nom.toLowerCase().replace(/\s+/g, '-')}`}
                                  onClick={() => handleEditUnitPrice(article)}
                                  className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFC700] hover:to-[#FF9500] text-[#0B132B] font-bold"
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  Modifier le Prix
                                </Button>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-[#EAEAEA]/60">Aucun article à prix fixe</div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Historique des modifications */}
          <Card className="glass-effect border-[#FFD700]/20">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-[#FFD700] text-xl font-bold flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Historique des Modifications
                </CardTitle>
                <Button
                  onClick={fetchPricingHistory}
                  variant="outline"
                  className="border-[#FFD700]/30 text-[#FFD700]"
                >
                  Actualiser
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pricingHistory.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {pricingHistory.map((h) => (
                    <div key={h.id} className="bg-[#1C2541]/50 border border-[#FFD700]/20 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#FFD700] font-bold">{h.gameName}</span>
                        {h.duration > 0 && <span className="text-[#EAEAEA]/50 text-sm">({h.duration} min)</span>}
                      </div>
                      <div className="text-sm text-[#EAEAEA]/70">
                        <span className="line-through text-red-400">{h.oldPrice?.toLocaleString()} F</span>
                        <span className="mx-2">&rarr;</span>
                        <span className="text-[#10B981] font-bold">{h.newPrice?.toLocaleString()} F</span>
                      </div>
                      <div className="text-xs text-[#EAEAEA]/50 mt-1">
                        {h.reason} &bull; {new Date(h.changedAt).toLocaleString('fr-FR')}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#EAEAEA]/60">
                  Aucun historique disponible
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Utilisateurs Connectés */}
        <TabsContent value="connected" className="space-y-4">
          <Card className="glass-effect border-[#10B981]/20">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-[#10B981] text-2xl font-bold flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Utilisateurs Connectés ({connectedUsers.length})
                </CardTitle>
                <Button
                  onClick={fetchAll}
                  variant="outline"
                  className="border-[#10B981]/30 text-[#10B981]"
                >
                  Actualiser
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {connectedUsers.length > 0 ? (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {connectedUsers.map((u) => (
                    <div key={u.uid} className="bg-[#1C2541]/50 border border-[#10B981]/30 rounded-lg p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#10B981] to-[#059669] flex items-center justify-center text-white font-bold">
                        {(u.nom || u.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#EAEAEA] font-bold">{u.nom || u.email}</p>
                        <p className="text-[#EAEAEA]/50 text-sm">{u.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse"></span>
                          <span className="text-[#10B981] text-xs font-semibold">{u.role}</span>
                          {u.sessions > 1 && <span className="text-[#EAEAEA]/40 text-xs">({u.sessions} sessions)</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#EAEAEA]/60">Aucun utilisateur connecté</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#C6A15B]">
              {formData.id ? 'Modifier' : 'Nouveau'} {dialogType}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {dialogType === 'rubrique' && (
              <>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    required
                    value={formData.nom || ''}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                      <SelectItem value="UNITAIRE">UNITAIRE</SelectItem>
                      <SelectItem value="TEMPS">TEMPS</SelectItem>
                      <SelectItem value="LIBRE">LIBRE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {dialogType === 'article' && (
              <>
                {/* CEO Session 8 — Toggle Type */}
                {!formData.id && (
                  <div className="grid grid-cols-2 gap-2 p-1 bg-[#0B132B] border border-[#C6A15B]/30 rounded-lg">
                    <button
                      type="button"
                      data-testid="article-type-console"
                      onClick={() => setArticleSubType('console')}
                      className={`py-2 px-3 rounded-md text-sm font-bold transition ${articleSubType === 'console' ? 'bg-[#FFD700] text-[#0B132B]' : 'text-[#EAEAEA]/60 hover:text-[#EAEAEA]'}`}
                    >
                      Console (tranches horaires)
                    </button>
                    <button
                      type="button"
                      data-testid="article-type-simple"
                      onClick={() => setArticleSubType('simple')}
                      className={`py-2 px-3 rounded-md text-sm font-bold transition ${articleSubType === 'simple' ? 'bg-[#C6A15B] text-[#0B132B]' : 'text-[#EAEAEA]/60 hover:text-[#EAEAEA]'}`}
                    >
                      Article simple (prix fixe)
                    </button>
                  </div>
                )}
                {formData.id && (
                  <div className="text-xs text-[#EAEAEA]/60 italic">
                    Type : {articleSubType === 'console' ? 'Console (tranches horaires)' : 'Article simple (prix fixe)'}
                  </div>
                )}
                
                <div>
                  <Label>Nom *</Label>
                  <Input
                    data-testid="article-nom-input"
                    required
                    value={formData.nom || ''}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder={articleSubType === 'console' ? 'Ex: PS6, Meta Quest 4...' : 'Ex: Heineken, Eau minérale...'}
                  />
                </div>
                
                {articleSubType === 'simple' && (
                  <>
                    <div>
                      <Label>Rubrique *</Label>
                      <Select
                        value={formData.rubriqueId}
                        onValueChange={(value) => setFormData({ ...formData, rubriqueId: value })}
                      >
                        <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                          {rubriques.filter(r => r.nom?.toUpperCase() !== 'JEUX').map(r => (
                            <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Prix Unitaire (FCFA)</Label>
                      <Input
                        data-testid="article-prix-unit-input"
                        type="number"
                        value={formData.prixUnit || ''}
                        onChange={(e) => setFormData({ ...formData, prixUnit: parseInt(e.target.value) })}
                        className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                      />
                    </div>
                    <div>
                      <Label>Stock Initial (pour les boissons)</Label>
                      <Input
                        type="number"
                        value={formData.stock || ''}
                        onChange={(e) => setFormData({ ...formData, stock: parseInt(e.target.value) || 0 })}
                        className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                        placeholder="Nombre de bouteilles en stock"
                      />
                    </div>
                    <div>
                      <Label>Coût d'Achat (FCFA)</Label>
                      <Input
                        type="number"
                        value={formData.coutAchat || ''}
                        onChange={(e) => setFormData({ ...formData, coutAchat: parseInt(e.target.value) || 0 })}
                        className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                        placeholder="Prix d'achat unitaire"
                      />
                    </div>
                  </>
                )}
                
                {articleSubType === 'console' && (
                  <>
                    <div className="flex items-center justify-between bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-lg px-3 py-2">
                      <span className="text-xs text-[#FFD700]/80 flex items-center gap-1.5">
                        <Gamepad2 className="w-3.5 h-3.5" />
                        Rubrique verrouillée : <strong>JEUX</strong> (apparaît dans Ventes & Prestations → Jeux)
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-[#FFD700]">Tranches horaires (durée / prix) *</Label>
                        <Button
                          type="button"
                          data-testid="add-tranche-draft-btn"
                          size="sm"
                          onClick={() => setTranchesDraft([...tranchesDraft, { duree: '', prix: '' }])}
                          className="bg-[#10B981]/20 hover:bg-[#10B981]/30 text-[#10B981] border border-[#10B981]/40 h-7 px-2 text-xs"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Ajouter
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {tranchesDraft.map((row, i) => (
                          <div key={i} className="flex gap-2 items-center">
                            <Input
                              data-testid={`tranche-duree-${i}`}
                              type="number"
                              step="10"
                              min="10"
                              max="480"
                              placeholder="Durée (min)"
                              value={row.duree}
                              onChange={(e) => {
                                const copy = [...tranchesDraft];
                                copy[i] = { ...copy[i], duree: e.target.value };
                                setTranchesDraft(copy);
                              }}
                              className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA] flex-1"
                            />
                            <Input
                              data-testid={`tranche-prix-${i}`}
                              type="number"
                              step="100"
                              min="100"
                              placeholder="Prix (FCFA)"
                              value={row.prix}
                              onChange={(e) => {
                                const copy = [...tranchesDraft];
                                copy[i] = { ...copy[i], prix: e.target.value };
                                setTranchesDraft(copy);
                              }}
                              className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA] flex-1"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={tranchesDraft.length <= 1}
                              onClick={() => {
                                if (tranchesDraft.length <= 1) return;
                                setTranchesDraft(tranchesDraft.filter((_, idx) => idx !== i));
                              }}
                              className="border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 h-9 w-9 p-0"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-[#EAEAEA]/50 mt-2">
                        Durée : multiple de 10, entre 10 et 480 min · Prix : multiple de 100 FCFA
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
            {dialogType === 'jeu' && (
              <>
                <div>
                  <Label>Nom du Jeu *</Label>
                  <Input
                    required
                    value={formData.nom || ''}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder="Ex: FC 24, GTA V, Beat Saber..."
                  />
                </div>
                <div>
                  <Label>Console *</Label>
                  <Select
                    value={formData.console}
                    onValueChange={(value) => setFormData({ ...formData, console: value })}
                  >
                    <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                      <SelectValue placeholder="Sélectionner une console" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                      <SelectItem value="PS4">PS4</SelectItem>
                      <SelectItem value="PS5">PS5</SelectItem>
                      <SelectItem value="Quest 3">Meta Quest 3</SelectItem>
                      <SelectItem value="VR2">PS VR2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prix par Session (FCFA)</Label>
                  <Input
                    type="number"
                    value={formData.prix || ''}
                    onChange={(e) => setFormData({ ...formData, prix: parseInt(e.target.value) || 0 })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder="Ex: 3000"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.actif || false}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label>Jeu actif</Label>
                </div>
              </>
            )}
            {dialogType === 'poste' && (
              <>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    required
                    value={formData.nom || ''}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  />
                </div>
                <div>
                  <Label>Plateforme</Label>
                  <Input
                    value={formData.plateforme || ''}
                    onChange={(e) => setFormData({ ...formData, plateforme: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  />
                </div>
              </>
            )}
            {dialogType === 'user' && (
              <>
                <div>
                  <Label>Nom complet *</Label>
                  <Input
                    required
                    value={formData.displayName || ''}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder="Ex: Jean Dupont"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    required
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder="Ex: jean@playcity.com"
                  />
                </div>
                <div>
                  <Label>Mot de passe *</Label>
                  <Input
                    required
                    type="password"
                    value={formData.password || ''}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                    placeholder="Min 6 caractères"
                  />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="cashier">Caissier</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <Button
              type="submit"
              className="w-full bg-[#C6A15B] hover:bg-[#B89149] text-[#0B132B]"
            >
              Enregistrer
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              Êtes-vous sûr de vouloir supprimer cet utilisateur ?
            </p>
            {selectedUser && (
              <div className="bg-[#0B132B] p-3 rounded">
                <p className="text-[#C6A15B] font-bold">{selectedUser.displayName}</p>
                <p className="text-[#EAEAEA]/60 text-sm">{selectedUser.email}</p>
                <p className="text-[#EAEAEA]/60 text-sm capitalize">Rôle: {selectedUser.role}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteUserDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteUser}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Commercial Dialog */}
      <Dialog open={showAddCommercialDialog} onOpenChange={setShowAddCommercialDialog}>
        <DialogContent 
          className="bg-[#1C2541] border-[#FFD700]/30 text-[#EAEAEA] max-w-2xl"
          style={{
            maxHeight: '90vh',
            padding: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ padding: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255, 215, 0, 0.2)', flexShrink: 0 }}>
            <DialogTitle className="text-[#FFD700] text-2xl font-bold">➕ Ajouter un Commercial</DialogTitle>
          </div>
          
          <div 
            ref={scrollContainerRef}
            style={{
              flex: '1 1 auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '1.5rem',
              minHeight: 0,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nom complet */}
                <div className="md:col-span-2">
                  <Label className="text-[#FFD700] font-semibold">Nom complet *</Label>
                  <Input
                    value={commercialForm.nom}
                    onChange={(e) => setCommercialForm({...commercialForm, nom: e.target.value})}
                    placeholder="Ex: Emmanuel ZIRE"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>

                {/* Poste */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Poste *</Label>
                  <Select 
                    value={commercialForm.poste} 
                    onValueChange={(value) => setCommercialForm({...commercialForm, poste: value})}
                  >
                    <SelectTrigger className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] border-[#FFD700]/30">
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Commercial Senior">Commercial Senior</SelectItem>
                      <SelectItem value="Vendeur">Vendeur</SelectItem>
                      <SelectItem value="PlayMaster">PlayMaster</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date de début */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Date de début *</Label>
                  <Input
                    type="date"
                    value={commercialForm.dateRecrutement}
                    onChange={(e) => setCommercialForm({...commercialForm, dateRecrutement: e.target.value})}
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>

                {/* Objectif mensuel */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Objectif mensuel (FCFA) *</Label>
                  <Input
                    type="number"
                    value={commercialForm.objectifCA}
                    onChange={(e) => setCommercialForm({...commercialForm, objectifCA: e.target.value})}
                    placeholder="Ex: 300000"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>

                {/* Salaire fixe */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Salaire fixe (FCFA) *</Label>
                  <Input
                    type="number"
                    value={commercialForm.salaireFix}
                    onChange={(e) => setCommercialForm({...commercialForm, salaireFix: e.target.value})}
                    placeholder="Ex: 60000"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>

                {/* Salaire brut */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Salaire brut (FCFA) *</Label>
                  <Input
                    type="number"
                    value={commercialForm.salaireBrut}
                    onChange={(e) => setCommercialForm({...commercialForm, salaireBrut: e.target.value})}
                    placeholder="Ex: 75000"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>

                {/* Téléphone */}
                <div>
                  <Label className="text-[#FFD700] font-semibold">Numéro de téléphone</Label>
                  <Input
                    type="tel"
                    value={commercialForm.telephone}
                    onChange={(e) => setCommercialForm({...commercialForm, telephone: e.target.value})}
                    placeholder="Ex: +225 0123456789"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700]"
                  />
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '1.5rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 215, 0, 0.2)', flexShrink: 0, display: 'flex', gap: '0.75rem' }}>
            <Button
              onClick={() => setShowAddCommercialDialog(false)}
              className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30"
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddCommercial}
              className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-bold"
            >
              ✅ Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Commercial Dialog */}
      <Dialog open={showDeleteCommercialDialog} onOpenChange={setShowDeleteCommercialDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-6 h-6" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA] text-lg">
              <strong>Voulez-vous vraiment supprimer ce commercial ?</strong>
            </p>
            <p className="text-red-400 font-semibold">
              ⚠️ Cette action est irréversible.
            </p>
            {selectedCommercial && (
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-red-500/30">
                <p className="text-[#FFD700] font-bold text-lg">{selectedCommercial.nom}</p>
                <p className="text-[#EAEAEA]/60 text-sm mt-1">
                  {selectedCommercial.poste || 'Commercial'}
                </p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteCommercialDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteCommercial}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold"
              >
                🗑️ Oui, supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Dialog */}
      <Dialog open={showDeleteCategoryDialog} onOpenChange={setShowDeleteCategoryDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              Êtes-vous sûr de vouloir supprimer ce type de dépense ?
            </p>
            {selectedCategory && (
              <div className="bg-[#0B132B] p-3 rounded">
                <p className="text-[#C6A15B] font-bold">{selectedCategory.nom}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteCategoryDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteCategory}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Article Dialog */}
      <Dialog open={showDeleteArticleDialog} onOpenChange={setShowDeleteArticleDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              Êtes-vous sûr de vouloir supprimer cet article ?
            </p>
            {selectedArticle && (
              <div className="bg-[#0B132B] p-3 rounded">
                <p className="text-[#C6A15B] font-bold">{selectedArticle.nom}</p>
                {selectedArticle.prixUnit && <p className="text-[#EAEAEA]/60 text-sm">Prix: {selectedArticle.prixUnit} FCFA</p>}
              </div>
            )}
            <div className="bg-yellow-900/20 border border-yellow-600/30 p-3 rounded">
              <p className="text-yellow-400 text-sm">
                ⚠️ Cet article disparaîtra du menu "Vendre"
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteArticleDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteArticle}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Rubrique Dialog */}
      <Dialog open={showDeleteRubriqueDialog} onOpenChange={setShowDeleteRubriqueDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-400">Rubrique contient des articles</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              La rubrique <strong className="text-[#C6A15B]">"{selectedRubrique?.nom}"</strong> contient {rubriqueArticles.length} article(s).
            </p>
            
            <div className="bg-[#0B132B] p-3 rounded">
              <p className="text-[#EAEAEA] font-bold mb-2">Articles concernés:</p>
              <ul className="text-[#EAEAEA]/80 text-sm space-y-1">
                {rubriqueArticles.map(art => (
                  <li key={art.id}>• {art.nom}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <Label className="text-[#EAEAEA] font-bold">Que faire avec ces articles ?</Label>
              
              {/* Option 1: Sélectionner rubrique existante */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="rubrique-option"
                    checked={!createNewRubrique}
                    onChange={() => setCreateNewRubrique(false)}
                    className="w-4 h-4"
                  />
                  <Label className="text-[#EAEAEA]">Déplacer vers une rubrique existante</Label>
                </div>
                
                {!createNewRubrique && (
                  <Select value={newRubriqueForArticles} onValueChange={setNewRubriqueForArticles}>
                    <SelectTrigger className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]">
                      <SelectValue placeholder="Choisir une rubrique..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C2541] text-[#EAEAEA]">
                      {rubriques.filter(r => r.id !== selectedRubrique?.id).map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Option 2: Créer nouvelle rubrique */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="rubrique-option"
                    checked={createNewRubrique}
                    onChange={() => setCreateNewRubrique(true)}
                    className="w-4 h-4"
                  />
                  <Label className="text-[#EAEAEA]">Créer une nouvelle rubrique</Label>
                </div>
                
                {createNewRubrique && (
                  <Input
                    placeholder="Nom de la nouvelle rubrique"
                    value={newRubriqueName}
                    onChange={(e) => setNewRubriqueName(e.target.value)}
                    className="bg-[#0B132B] border-[#C6A15B]/30 text-[#EAEAEA]"
                  />
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDeleteRubriqueDialog(false);
                  setNewRubriqueForArticles('');
                  setCreateNewRubrique(false);
                  setNewRubriqueName('');
                }}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteRubrique}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer la rubrique
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Jeu Dialog */}
      <Dialog open={showDeleteJeuDialog} onOpenChange={setShowDeleteJeuDialog}>
        <DialogContent className="bg-[#1C2541] border-[#C6A15B]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[#EAEAEA]">
              Êtes-vous sûr de vouloir supprimer ce jeu ?
            </p>
            {selectedJeu && (
              <div className="bg-[#0B132B] p-3 rounded">
                <p className="text-[#C6A15B] font-bold">{selectedJeu.nom}</p>
                <p className="text-[#EAEAEA]/60 text-sm">Console: {selectedJeu.console}</p>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteJeuDialog(false)}
                className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA]"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmDeleteJeu}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Supprimer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Pricing Dialog */}
      <Dialog open={showEditPricingDialog} onOpenChange={setShowEditPricingDialog}>
        <DialogContent className="bg-[#1C2541] border-[#FFD700]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700] text-xl font-bold flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier le Prix
            </DialogTitle>
          </DialogHeader>
          {selectedPricing && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-[#FFD700]/30">
                <div className="flex items-center gap-2 mb-2">
                  <Gamepad2 className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-[#FFD700] font-bold text-lg">{selectedPricing.articleName}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[#EAEAEA]/70">Durée actuelle:</span>
                  <span className="text-[#EAEAEA] font-bold">{formatDuree(selectedPricing.duration)} ({selectedPricing.duration} min)</span>
                </div>
                <div className="flex justify-between items-center text-sm mt-1">
                  <span className="text-[#EAEAEA]/70">Prix actuel:</span>
                  <span className="text-[#10B981] font-bold text-lg">{selectedPricing.currentPrice?.toLocaleString()} F</span>
                </div>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Nouvelle Durée (minutes) *</Label>
                <Input
                  data-testid="pricing-new-duration-input"
                  type="number"
                  step="10"
                  min="10"
                  max="480"
                  value={pricingForm.duration}
                  onChange={(e) => setPricingForm({...pricingForm, duration: e.target.value})}
                  placeholder="Multiple de 10 minutes (10 à 480)"
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                />
                <p className="text-xs text-[#EAEAEA]/50 mt-1">
                  Multiple de 10 minutes — Min 10, Max 480
                  {pricingForm.duration && parseInt(pricingForm.duration) >= 10 && parseInt(pricingForm.duration) % 10 === 0 && (
                    <span className="text-[#10B981] ml-2">→ {formatDuree(pricingForm.duration)}{getDureeSubLabel(pricingForm.duration) ? ` (${getDureeSubLabel(pricingForm.duration)})` : ''}</span>
                  )}
                </p>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Nouveau Prix (FCFA) *</Label>
                <Input
                  data-testid="pricing-new-price-input"
                  type="number"
                  step="100"
                  min="100"
                  value={pricingForm.price}
                  onChange={(e) => setPricingForm({...pricingForm, price: e.target.value})}
                  placeholder="Multiple de 100 FCFA"
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                />
                <p className="text-xs text-[#EAEAEA]/50 mt-1">Multiple de 100 FCFA</p>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Raison de la modification *</Label>
                <Select 
                  value={pricingForm.reason} 
                  onValueChange={(value) => setPricingForm({...pricingForm, reason: value})}
                >
                  <SelectTrigger className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C2541] border-[#FFD700]/30">
                    <SelectItem value="Promotion">Promotion</SelectItem>
                    <SelectItem value="Ajustement">Ajustement</SelectItem>
                    <SelectItem value="Normal">Retour Prix Normal</SelectItem>
                    <SelectItem value="Augmentation">Augmentation</SelectItem>
                    <SelectItem value="Réduction">Réduction</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => setShowEditPricingDialog(false)}
                  className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30"
                >
                  Annuler
                </Button>
                <Button
                  data-testid="save-pricing-btn"
                  onClick={handleUpdatePricing}
                  className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-bold"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Unit Price Dialog (BOISSON, NESPRESSO, CASINO) */}
      <Dialog open={showEditUnitPriceDialog} onOpenChange={setShowEditUnitPriceDialog}>
        <DialogContent className="bg-[#1C2541] border-[#FFD700]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700] text-xl font-bold flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier le Prix
            </DialogTitle>
          </DialogHeader>
          {selectedUnitArticle && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-[#FFD700]/30">
                <p className="text-[#FFD700] font-bold text-lg">{selectedUnitArticle.nom}</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-[#EAEAEA]/70">Prix actuel:</span>
                  <span className="text-[#10B981] font-bold text-lg">{selectedUnitArticle.prixUnit?.toLocaleString()} F</span>
                </div>
              </div>
              <div>
                <Label className="text-[#FFD700] font-semibold">Nouveau Prix (FCFA) *</Label>
                <Input
                  data-testid="unit-price-input"
                  type="number"
                  step="100"
                  min="100"
                  value={unitPriceForm.price}
                  onChange={(e) => setUnitPriceForm({...unitPriceForm, price: e.target.value})}
                  placeholder="Multiple de 100 FCFA"
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                />
                <p className="text-xs text-[#EAEAEA]/50 mt-1">Multiple de 100 FCFA</p>
              </div>
              <div>
                <Label className="text-[#FFD700] font-semibold">Raison</Label>
                <Select value={unitPriceForm.reason} onValueChange={(v) => setUnitPriceForm({...unitPriceForm, reason: v})}>
                  <SelectTrigger className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1C2541] border-[#FFD700]/30">
                    <SelectItem value="Promotion">Promotion</SelectItem>
                    <SelectItem value="Ajustement">Ajustement</SelectItem>
                    <SelectItem value="Augmentation">Augmentation</SelectItem>
                    <SelectItem value="Réduction">Réduction</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowEditUnitPriceDialog(false)} className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30">Annuler</Button>
                <Button data-testid="save-unit-price-btn" onClick={handleUpdateUnitPrice} className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-bold">Enregistrer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Duration Dialog (CEO : ajout de tranche) */}
      <Dialog open={showAddDurationDialog} onOpenChange={setShowAddDurationDialog}>
        <DialogContent className="bg-[#1C2541] border-[#10B981]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#10B981] text-xl font-bold flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Ajouter une tranche
            </DialogTitle>
          </DialogHeader>
          {addDurationArticle && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-[#10B981]/30">
                <div className="flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5 text-[#FFD700]" />
                  <span className="text-[#FFD700] font-bold text-lg">{addDurationArticle.nom}</span>
                </div>
                <p className="text-xs text-[#EAEAEA]/60 mt-2">
                  Tranches existantes : {Object.keys(addDurationArticle.prixParDuree || {}).sort((a,b)=>parseInt(a)-parseInt(b)).map(k => formatDuree(k)).join(' • ')}
                </p>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Durée (minutes) *</Label>
                <Input
                  data-testid="add-duration-input"
                  type="number"
                  step="10"
                  min="10"
                  max="480"
                  value={addDurationForm.duration}
                  onChange={(e) => setAddDurationForm({...addDurationForm, duration: e.target.value})}
                  placeholder="Multiple de 10 minutes (10 à 480)"
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                />
                <p className="text-xs text-[#EAEAEA]/50 mt-1">
                  Multiple de 10 minutes — Min 10, Max 480
                  {addDurationForm.duration && parseInt(addDurationForm.duration) >= 10 && parseInt(addDurationForm.duration) % 10 === 0 && (
                    <span className="text-[#10B981] ml-2">→ {formatDuree(addDurationForm.duration)}{getDureeSubLabel(addDurationForm.duration) ? ` (${getDureeSubLabel(addDurationForm.duration)})` : ''}</span>
                  )}
                </p>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Prix (FCFA) *</Label>
                <Input
                  data-testid="add-duration-price-input"
                  type="number"
                  step="100"
                  min="100"
                  value={addDurationForm.price}
                  onChange={(e) => setAddDurationForm({...addDurationForm, price: e.target.value})}
                  placeholder="Multiple de 100 FCFA"
                  className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                />
                <p className="text-xs text-[#EAEAEA]/50 mt-1">Multiple de 100 FCFA</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowAddDurationDialog(false)} className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30">Annuler</Button>
                <Button
                  data-testid="save-add-duration-btn"
                  onClick={handleSubmitAddDuration}
                  className="flex-1 bg-gradient-to-r from-[#10B981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white font-bold"
                >
                  Ajouter
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog (CEO Session 6) */}
      <Dialog open={showChangeRoleDialog} onOpenChange={setShowChangeRoleDialog}>
        <DialogContent className="bg-[#1C2541] border-[#FFD700]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#FFD700] text-xl font-bold flex items-center gap-2">
              <User className="w-5 h-5" />
              Changer le rôle
            </DialogTitle>
          </DialogHeader>
          {roleChangeTarget && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-[#FFD700]/30">
                <p className="text-[#EAEAEA] font-bold">{roleChangeTarget.displayName}</p>
                <p className="text-[#EAEAEA]/60 text-sm">{roleChangeTarget.email}</p>
                <p className="text-[#C6A15B] text-sm mt-2">
                  Rôle actuel : <span className="font-bold">
                    {{ admin: 'Admin', cashier: 'Manager (Cashier)', staff: 'Commercial (Staff)' }[roleChangeTarget.role] || roleChangeTarget.role}
                  </span>
                </p>
              </div>

              <div>
                <Label className="text-[#FFD700] font-semibold">Nouveau rôle *</Label>
                <Select value={roleChangeValue} onValueChange={setRoleChangeValue}>
                  <SelectTrigger
                    data-testid="change-role-select"
                    className="bg-[#0B132B] border-[#FFD700]/30 text-[#EAEAEA] focus:border-[#FFD700] text-lg font-bold"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C2541] border-[#FFD700]/30 text-[#EAEAEA]">
                    <SelectItem value="admin">Admin — accès total</SelectItem>
                    <SelectItem value="cashier">Manager (Cashier) — voit toute l'équipe</SelectItem>
                    <SelectItem value="staff">Commercial (Staff / Playmaster)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#EAEAEA]/60 mt-2 italic">
                  Hiérarchie : Admin &gt; Manager (Cashier) &gt; Commercial (Staff)
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowChangeRoleDialog(false)} className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30">Annuler</Button>
                <Button
                  data-testid="confirm-change-role-btn"
                  onClick={handleConfirmChangeRole}
                  className="flex-1 bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFC700] hover:to-[#FF9500] text-[#0B132B] font-bold"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Duration Confirmation Dialog (CEO) */}
      <Dialog open={showDeleteDurationDialog} onOpenChange={setShowDeleteDurationDialog}>
        <DialogContent className="bg-[#1C2541] border-[#EF4444]/30 text-[#EAEAEA]">
          <DialogHeader>
            <DialogTitle className="text-[#EF4444] text-xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          {deleteDurationTarget && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0B132B] p-4 rounded-lg border-2 border-[#EF4444]/30">
                <p className="text-[#EAEAEA]">
                  Confirmer la suppression de cette tranche ?
                </p>
                <p className="text-[#FFD700] font-bold mt-2">
                  {deleteDurationTarget.articleName} — {formatDuree(deleteDurationTarget.duration)} ({deleteDurationTarget.duration} min)
                </p>
                <p className="text-[#EAEAEA]/60 text-sm mt-3 italic">
                  Les ventes passées ne seront pas modifiées.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={() => setShowDeleteDurationDialog(false)} className="flex-1 bg-[#1C2541] hover:bg-[#C6A15B]/20 text-[#EAEAEA] border border-[#C6A15B]/30">Annuler</Button>
                <Button
                  data-testid="confirm-delete-duration-btn"
                  onClick={handleConfirmDeleteDuration}
                  className="flex-1 bg-gradient-to-r from-[#EF4444] to-[#DC2626] hover:from-[#DC2626] hover:to-[#B91C1C] text-white font-bold"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
